import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import CelebrationScreen from 'shared/patterns/vxl/CelebrationScreen/CelebrationScreen';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const RepaymentSuccessScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'repaymentSuccess',
  });

  const { bottom } = useSafeAreaInsets();

  const backToActiveDetails = () => {
    if (router.canGoBack()) {
      router.dismissAll();
    }
    router.navigate('/varo-line-of-credit/active-details');
  };

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHARGE_CARD,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_SAVINGS,
    HomeTileTypeIds.VLOC_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_TRACKER_CARD_TILE_CREDIT_TRACKER,
  ]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const {
    captionText,
    headlineText,
    messageText,
    noteText,
    outstandingBalance,
  } = useLocalSearchParams<{
    captionText: string;
    headlineText: string;
    messageText: string;
    noteText: string;
    outstandingBalance: string;
  }>();

  const dismissToTab = useDismissToRootTab();

  const handleBack = () => {
    if (Number(outstandingBalance) !== 0) {
      backToActiveDetails();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  useBackHandler(handleBack);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      {Number(outstandingBalance) !== 0 ? (
        <SuccessScreen
          primaryButton={{
            label: translatedText('button'),
            analyticsId: 'repaymentGoToHome',
            customAttributes: { cta: 'Done' },
            onPress: backToActiveDetails,
          }}
          screenAnalyticsEventKey="/varo-line-of-credit/repayment-success/vlocRepaymentSuccessful"
          visual="CASH_A"
          eyebrowText={captionText}
          titleText={headlineText}
          content={
            <View style={styles.contentContainer}>
              <BodyBaseText numberOfLines={0} style={styles.message}>
                {messageText}
              </BodyBaseText>
              <BodyXSText style={styles.noteText}>{noteText}</BodyXSText>
            </View>
          }
        />
      ) : (
        <ScrollView contentContainerStyle={styles.scrollView}>
          <CelebrationScreen
            illustrationKey="PAID_OFF_A_ANIMATED"
            illustrationAccessibilityLabel="You are all paid off celebration animation"
            backgroundTexture={
              // eslint-disable-next-line @typescript-eslint/no-require-imports
              require('assets/images/vxl/component/celebrationScreen/background-texture-light.png') as ImageSourcePropType
            }
            bodyText={messageText}
            captionText={noteText}
            button={{
              label: translatedText('button'),
              onPress: () => {
                dismissToTab(Tabs.HOME);
              },
            }}
            screenAnalyticsEventKey="/varo-line-of-credit/repayment-success/vlocPaidOff"
          />
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    gap: theme.spacing.m,
  },
  message: {
    textAlign: 'center',
  },
  noteText: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  scrollView: { flex: 1 },
});

export default RepaymentSuccessScreen;
